import { Button, Result } from 'antd';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
}

function ErrorFallback({ message, onReload }: { message?: string; onReload: () => void }) {
  const { t } = useTranslation('app');
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--ds-bg-canvas)',
      }}
    >
      <Result
        status="error"
        title={t('error.title')}
        subTitle={message ?? t('error.subTitle')}
        extra={
          <Button type="primary" onClick={onReload}>
            {t('error.reload')}
          </Button>
        }
      />
    </div>
  );
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private handleReload = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorFallback message={this.state.error?.message} onReload={this.handleReload} />;
    }
    return this.props.children;
  }
}
